import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiArrowRight } = FiIcons;

const ROUTES = [
  { path: '/', label: 'Today', desc: 'Your day at a glance' },
  { path: '/plan', label: 'Plan', desc: 'Organize tasks and projects' },
  { path: '/studio', label: 'Studio', desc: 'Focus and create art' },
  { path: '/garden', label: 'Garden', desc: 'Build consistent habits' },
  { path: '/journal', label: 'Journal', desc: 'Write notes, ideas, and reflections' },
  { path: '/membership', label: 'Membership', desc: 'Free and Studio Plus access' },
  { path: '/settings', label: 'Settings', desc: 'Profile, backup, and recovery' },
  { path: '/analytics', label: 'Your progress', desc: 'Focus, art, tasks, and habits' },
  { path: '/welcome', label: 'About MindGarden', desc: 'Learn how MindGarden works' },
];

export default function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Use a native event listener to bypass any React event delegation blockers
  const setInputRef = React.useCallback((node) => {
    inputRef.current = node;
    if (node) {
      // Auto-focus with slight delay to ensure modal is ready
      setTimeout(() => node.focus(), 50);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      setQuery('');
      setSelectedIndex(0);

      const syncQuery = () => {
        if (inputRef.current) {
          const val = inputRef.current.value;
          setQuery((prev) => (prev !== val ? val : prev));
        }
      };

      const intervalId = setInterval(syncQuery, 100);
      const node = inputRef.current;
      if (node) {
        node.addEventListener('input', syncQuery);
        node.addEventListener('keyup', syncQuery);
      }

      return () => {
        clearInterval(intervalId);
        if (node) {
          node.removeEventListener('input', syncQuery);
          node.removeEventListener('keyup', syncQuery);
        }
      };
    }
  }, [isOpen]);

  const filteredRoutes = ROUTES.filter(
    (route) =>
      route.label.toLowerCase().includes(query.toLowerCase()) ||
      route.desc.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredRoutes.length > 0) {
        setSelectedIndex((prev) => (prev + 1) % filteredRoutes.length);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredRoutes.length > 0) {
        setSelectedIndex((prev) => (prev - 1 + filteredRoutes.length) % filteredRoutes.length);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const currentQuery = inputRef.current ? inputRef.current.value : query;
      const currentFiltered = ROUTES.filter(
        (route) =>
          route.label.toLowerCase().includes(currentQuery.toLowerCase()) ||
          route.desc.toLowerCase().includes(currentQuery.toLowerCase())
      );
      if (currentFiltered.length > 0) {
        const safeIndex = Math.max(0, Math.min(selectedIndex, currentFiltered.length - 1));
        navigate(currentFiltered[safeIndex].path);
        setIsOpen(false);
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-pine/40 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.15 }}
            className="relative w-full max-w-xl bg-paper rounded-3xl shadow-2xl border border-pine/10 overflow-hidden flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center px-4 py-4 border-b border-pine/10 bg-white">
              <SafeIcon icon={FiSearch} className="w-5 h-5 text-pine/50 mr-3" />
              <input
                ref={setInputRef}
                type="text"
                className="flex-1 bg-transparent border-none outline-none text-lg text-pine placeholder-pine/40 font-dm-sans keyboard-event-protection"
                placeholder="Where to? (e.g. Focus, Garden)"
                defaultValue=""
                onKeyDown={handleKeyDown}
                autoComplete="off"
              />
              <span className="text-[10px] uppercase font-bold text-pine/40 tracking-wider bg-pine/5 px-2 py-1 rounded">ESC</span>
            </div>
            <div className="max-h-96 overflow-y-auto custom-scrollbar p-2 bg-paper/50">
              {filteredRoutes.length === 0 ? (
                <div className="p-8 text-center text-pine/50 font-dm-sans text-sm">No results found.</div>
              ) : (
                filteredRoutes.map((route, idx) => (
                  <button
                    key={route.path}
                    onClick={() => {
                      navigate(route.path);
                      setIsOpen(false);
                    }}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={`w-full flex items-center justify-between p-3 rounded-2xl transition-colors text-left ${
                      selectedIndex === idx ? 'bg-pine text-paper' : 'text-pine hover:bg-pine/5'
                    }`}
                  >
                    <div>
                      <div className="font-bold text-sm">{route.label}</div>
                      <div className={`text-xs ${selectedIndex === idx ? 'text-paper/70' : 'text-pine/50'}`}>{route.desc}</div>
                    </div>
                    {selectedIndex === idx && <SafeIcon icon={FiArrowRight} className="w-4 h-4 text-paper/70" />}
                  </button>
                ))
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}